import { useReducedMotion } from "@mantine/hooks";
import clsx from "clsx";
import { type JSX, type ReactNode, useLayoutEffect, useRef } from "react";

import { useSession, type VoiceTask } from "@/sdk";

import { useIsPacingActive } from "../hooks/useIsPacingActive";
import { usePacingPulseSync } from "../hooks/usePacingPulseSync";
import { type PacingVisualState, usePacingState } from "../hooks/usePacingState";
import classes from "./CallBarGlow.module.css";

interface CallBarGlowProps {
    task: VoiceTask;
    children: ReactNode;
}

const fillStateClasses: Record<PacingVisualState, string | undefined> = {
    neutral: undefined,
    onTrack: classes.fillOnTrack,
    approaching: classes.fillApproaching,
    exceeded: classes.fillExceeded,
};

/**
 * VCP 1.5 "Glow Bar": wraps the call controls bar in a state-colored fill that grows toward the target.
 * emitAnalytics=false because the countdown pill (passed in as `children`) is the sole emitter.
 */
export function CallBarGlow({ task, children }: CallBarGlowProps): JSX.Element {
    const isPacingActive = useIsPacingActive();
    const { startedTimestamp, id: sessionId } = useSession();
    // Every status where the call is over, so both fill paths stop on the same set. Narrower than
    // this (just wrapping/completed) leaves the growth transition running on a `missed` task, and
    // `status === "assigned"` is too narrow the other way: it stops the tick while a task is still
    // `pending`, which froze the reduced-motion fill. Local to the glow bar on purpose — the pill
    // and PacingBarOutline keep their own gating.
    const callOver =
        task.status === "wrapping" ||
        task.status === "completed" ||
        task.status === "cancelled" ||
        task.status === "missed";

    const pacing = usePacingState({
        partner: task.partner,
        sessionId,
        startedTimestamp,
        // Not `status === "assigned"`: the two fill paths read elapsed from different clocks — the
        // transition below reads `Date.now()` live, this reads `pacing.elapsedSeconds`. Ticking for
        // every not-over status keeps them on the same clock so reduced motion can't diverge.
        callActive: !callOver,
        isPacingActive,
        emitAnalytics: false,
    });

    // `hasTarget` alone, matching PacingBarOutline.
    const showPacing = pacing.hasTarget;
    const visualState: PacingVisualState = showPacing ? pacing.state : "neutral";

    const fillRef = useRef<HTMLDivElement>(null);
    const glowSyncRef = usePacingPulseSync(visualState === "exceeded" && !callOver);

    // Resolved on first render so a reduced-motion expert never sees a frame of the transition.
    const reducedMotion = useReducedMotion(undefined, { getInitialValueInEffect: false });

    // One long linear transition carries the fill instead of per-second steps, so elapsed is read
    // live rather than from the deps — this must re-arm on target/call changes only, never on a tick.
    // Re-running it on call end is also what FREEZES the fill, since re-snapping lands on the
    // position the linear transition had reached and the growth branch is then skipped.
    useLayoutEffect(() => {
        const el = fillRef.current;
        if (!el || !showPacing || pacing.targetSeconds <= 0) return;
        if (reducedMotion) return; // The per-tick effect below drives it instead.

        const elapsedSeconds = Math.max((Date.now() - startedTimestamp) / 1000, 0);
        const elapsedFraction = Math.min(elapsedSeconds / pacing.targetSeconds, 1);
        const remainingSeconds = Math.max(pacing.targetSeconds - elapsedSeconds, 0);

        el.style.transition = "none";
        el.style.transform = `scaleX(${elapsedFraction})`;
        void el.offsetWidth; // Force a reflow so the snap commits before the growth transition.

        if (!callOver && remainingSeconds > 0) {
            el.style.transition = `transform ${remainingSeconds}s linear, background-color 0.4s ease`;
            el.style.transform = "scaleX(1)";
        } else {
            el.style.transition = "background-color 0.4s ease";
        }
    }, [showPacing, callOver, pacing.targetSeconds, startedTimestamp, reducedMotion]);

    // No growth transition under reduced motion, so step the fill per tick instead. Ticking stops
    // when the call is over, which is what freezes it here.
    useLayoutEffect(() => {
        const el = fillRef.current;
        if (!el || !reducedMotion || !showPacing || pacing.targetSeconds <= 0) return;

        const elapsedFraction = Math.min(Math.max(pacing.elapsedSeconds, 0) / pacing.targetSeconds, 1);
        el.style.transition = "background-color 0.4s ease";
        el.style.transform = `scaleX(${elapsedFraction})`;
    }, [reducedMotion, showPacing, pacing.elapsedSeconds, pacing.targetSeconds]);

    return (
        <div
            ref={glowSyncRef}
            className={clsx(classes.wrapper, visualState === "exceeded" && classes.exceeded, callOver && classes.ended)}
            data-testid="call-bar-glow"
            data-pacing-state={visualState}
        >
            {showPacing ? (
                <div
                    ref={fillRef}
                    aria-hidden
                    className={clsx(classes.fill, fillStateClasses[visualState])}
                    data-testid="call-bar-glow-fill"
                />
            ) : null}
            <div className={classes.content}>{children}</div>
        </div>
    );
}
